import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button, Card, Space, Typography, App as AntApp } from 'antd';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';

type Canal = {
  status: 'pendente' | 'criando' | 'pronto' | 'erro';
  web_url: string | null;
  nome: string | null;
  ultimo_erro: string | null;
};

type TeamsStatus = {
  ativo: boolean;
  equipe_nome: string;
  pode_criar: boolean;
  canal: Canal | null;
  contrato_raiz_id: string;
};

function linkTeams(valor: string | null | undefined): string | null {
  if (!valor) return null;
  try {
    const url = new URL(valor);
    if (url.protocol !== 'https:' || url.username || url.password || url.port) return null;
    if (url.hostname !== 'teams.microsoft.com' && url.hostname !== 'teams.cloud.microsoft') return null;
    return url.href;
  } catch {
    return null;
  }
}

export function CanalTeams({ contratoId }: { contratoId: string }) {
  const { message } = AntApp.useApp();
  const [dados, setDados] = useState<TeamsStatus | null>(null);
  const [erroLeitura, setErroLeitura] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [tentativas, setTentativas] = useState(0);
  const geracao = useRef(0);
  const requisicao = useRef<AbortController | null>(null);

  const consultar = useCallback(async (action: 'op_teams_status' | 'op_teams_criar') => {
    const atual = geracao.current;
    requisicao.current?.abort();
    const controller = new AbortController();
    requisicao.current = controller;
    setCarregando(true);
    try {
      const token = await getToken();
      if (atual !== geracao.current || controller.signal.aborted) return;
      const resposta = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action, contrato_id: contratoId },
        { headers: { Authorization: token }, signal: controller.signal });
      if (atual !== geracao.current || controller.signal.aborted) return;
      const body = resposta.data?.body ? JSON.parse(resposta.data.body) : resposta.data;
      if (body?.ok === false || typeof body?.ativo !== 'boolean') throw new Error(body?.error || 'Resposta inválida');
      setDados(body as TeamsStatus);
      setErroLeitura(false);
    } catch (erro: any) {
      if (atual !== geracao.current || controller.signal.aborted) return;
      if (action === 'op_teams_status') {
        setErroLeitura(true);
      } else {
        message.error(erro.response?.data?.error || erro.message || 'Não foi possível criar o canal.');
      }
    } finally {
      if (atual === geracao.current && !controller.signal.aborted) setCarregando(false);
    }
  }, [contratoId, message]);

  useEffect(() => {
    geracao.current += 1;
    setDados(null);
    setErroLeitura(false);
    setTentativas(0);
    consultar('op_teams_status');
    return () => {
      geracao.current += 1;
      requisicao.current?.abort();
    };
  }, [consultar]);

  const status = dados?.canal?.status;
  useEffect(() => {
    // Consulta inicial + até cinco atualizações em ~45 segundos.
    if (erroLeitura || carregando || tentativas >= 5 || (status !== 'pendente' && status !== 'criando')) return;
    const timer = window.setTimeout(() => {
      setTentativas(n => n + 1);
      consultar('op_teams_status');
    }, 9000);
    return () => window.clearTimeout(timer);
  }, [carregando, consultar, erroLeitura, status, tentativas]);

  if (!dados && !erroLeitura) return null;
  if (dados && !dados.ativo && !dados.canal && !erroLeitura) return null;

  const link = linkTeams(dados?.canal?.web_url);
  const podeCriar = !!dados?.pode_criar;
  const acao = dados?.canal ? 'Tentar novamente' : 'Criar canal';

  return (
    <Card size="small" title="Conversa no Teams" style={{ gridColumn: '1 / -1' }}>
      <Space orientation="vertical" size={8} style={{ width: '100%' }}>
        {erroLeitura ? (
          <Typography.Text type="danger">Não foi possível consultar o canal do Teams.</Typography.Text>
        ) : status === 'pronto' ? (
          <Typography.Text>Canal {dados?.canal?.nome || 'do contrato'} na equipe Engenharia.</Typography.Text>
        ) : status === 'pendente' || status === 'criando' ? (
          <Typography.Text>Preparando o canal na equipe Engenharia…</Typography.Text>
        ) : status === 'erro' ? (
          <Typography.Text type="danger">Não foi possível preparar o canal.</Typography.Text>
        ) : (
          <Typography.Text>Este contrato ainda não tem canal na equipe Engenharia.</Typography.Text>
        )}
        <Space wrap>
          {!erroLeitura && status === 'pronto' && link && (
            <Button type="primary" href={link} target="_blank" rel="noopener noreferrer">Abrir conversa no Teams</Button>
          )}
          {!erroLeitura && status === 'pronto' && !link && (
            <Typography.Text type="secondary">O link da conversa ainda não está disponível.</Typography.Text>
          )}
          {!erroLeitura && podeCriar && status !== 'pronto' && (
            <Button type="primary" loading={carregando} onClick={() => {
              setTentativas(0);
              consultar('op_teams_criar');
            }}>{acao}</Button>
          )}
          {(erroLeitura || status === 'pendente' || status === 'criando') && (
            <Button loading={carregando} onClick={() => consultar('op_teams_status')}>Atualizar</Button>
          )}
        </Space>
      </Space>
    </Card>
  );
}
